import API from "./API";
import { logCaptureMessage } from "../utils/sentryLogger";

/**
 * Add products to Shopify cart using items array format
 * This supports Recharge dynamic bundles with parent + child items
 * @param {object} productsData - Contains items array and metadata
 */
export function addProductToCart(productsData) {
    const { items, deliveryDate, dietType } = productsData;
    
    if (!items || !Array.isArray(items) || items.length === 0) {
        console.error('[addProductToCart] Invalid items array');
        return;
    }

    const hasRechargeBundleRoot = items.some(item => 
        item.properties?._rc_bundle_root === "true"
    );

    if (hasRechargeBundleRoot) {
        if (process.env.NODE_ENV === "production") {
            API.post("/cart/add.js", JSON.stringify({ items }))
                .then((response) => {
                    
                    const cartAttributes = {
                        attributes: {
                            deliveryDate: deliveryDate || '',
                            dietType: dietType || ''
                        }
                    };
                    
                    return API.post("/cart/update.js", JSON.stringify(cartAttributes));
                })
                .then(() => {
                    window.location.href = "/cart";
                })
                .catch((error) => {
                    logCaptureMessage({
                        message: "Failed to add Recharge bundle to cart",
                        extra: {
                            error: error.message,
                            items: items
                        }
                    });
                });
        } else {
            console.log('Success');
        }
    } else {
        addProductsSequentially({ productsToAdd: items, deliveryDate, dietType });
    }
}

/**
 * Legacy method: Add products one by one (used for non-bundle items)
 */
function addProductsSequentially(productsData) {
    const products = [...productsData.productsToAdd];
    const deliveryDate = productsData.deliveryDate;
    const dietType = productsData.dietType;
    
    const cartAttributes = {
        attributes: {
            deliveryDate: deliveryDate || '',
            dietType: dietType || ''
        }
    };

    const chainedCall = () => {
        if (products.length === 0) {
            finalizeCart(cartAttributes);
            return;
        }

        const product = products.shift();
        
        if (!product || !product.id) {
            console.error('[addProductsSequentially] Invalid product, skipping:', product);
            chainedCall();
            return;
        }

        let cartData = {
            id: product.id,
            quantity: product.quantity || 1,
            properties: product.properties || {}
        };

        if (product.selling_plan) {
            cartData.selling_plan = product.selling_plan;
        }

        // 6-week interval products
        const sixWeekProducts = [
            "39990491021430", "39992546066550", "40055267885174", 
            "40055267950710", "44899639853355", "44799040749867",
            "44799042224427", "45049912197419", "40073304899702",
            "40073304965238", "47306732568875", "45179358216491",
            "45179487486251", "40272716333174"
        ];
        
        if (sixWeekProducts.includes(String(product.id))) {
            cartData.properties._shipping_interval_frequency = '6';
        }

        if (process.env.NODE_ENV === "production") {
            API.post("/cart/add.js", JSON.stringify(cartData))
                .then(() => {
                    chainedCall();
                })
                .catch((error) => {
                    chainedCall();
                });
        } else {
            chainedCall();
        }
    };

    const finalizeCart = (attributes) => {
        if (process.env.NODE_ENV === "production") {
            API.post("/cart/update.js", JSON.stringify(attributes))
                .then(() => {
                    window.location.href = "/cart";
                })
                .catch((error) => {
                    console.error(error);
                    window.location.href = "/cart";
                });
        }
    };

    chainedCall();
}

/**
 * Clear Shopify cart
 */
export function clearCart(callback) {
    if (process.env.NODE_ENV === "production") {
        API.post("/cart/clear.js")
            .then(() => {
                console.log('[clearCart] Cart cleared successfully');
                if (callback && typeof callback === 'function') {
                    callback();
                }
            })
            .catch((error) => {
                console.error('[clearCart] Failed:', error);
                if (callback && typeof callback === 'function') {
                    callback();
                }
            });
    } else {
        console.log('[clearCart][DEV] Would clear cart');
        if (callback && typeof callback === 'function') {
            callback();
        }
    }
} 